import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { ClerkProvider, SignIn, SignedIn, SignedOut, useAuth, useUser } from '@clerk/clerk-react';
import { heIL } from '@clerk/localizations';
import type { Me } from '@shared/types';
import './styles.css';
import { api, currentTenant, setCurrentTenant, setTokenGetter } from './api';
import { errorText, queryClient, ToastProvider, useMe } from './store';
import { App } from './App';
import { TenantsPanel } from './components/Settings';
import { LegalLinks, Spinner } from './components/ui';
import { GoogleAutoSignIn } from './components/GoogleAutoSignIn';
import { startPwa } from './pwa';

/** A thin strip on review environments, so a staging tab is never mistaken for the real thing. In the flow, so it covers nothing. */
function EnvBanner({ environment, readOnly }: { environment?: string; readOnly?: boolean }) {
  if (!environment || environment === 'production') return null;
  return (
    <div role="status" className="bg-amber-400 py-0.5 text-center text-[11px] font-semibold text-amber-950">
      סביבת בדיקה{readOnly ? ' · לקריאה בלבד, שינויים לא נשמרים' : ''}
    </div>
  );
}

function Root() {
  const [cfg, setCfg] = useState<{ clerkPublishableKey: string | null; devAuth: boolean; environment?: string; readOnly?: boolean } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { api.config().then(setCfg).catch((e) => setErr(String(e))); }, []);
  return <><EnvBanner environment={cfg?.environment} readOnly={cfg?.readOnly} /><RootBody cfg={cfg} err={err} /></>;
}

function RootBody({ cfg, err }: { cfg: { clerkPublishableKey: string | null; devAuth: boolean } | null; err: string | null }) {
  if (err) return <div className="p-6 text-danger">שגיאה בטעינה: {err}</div>;
  if (!cfg) return <div className="flex h-full items-center justify-center"><Spinner /></div>;
  if (cfg.devAuth || !cfg.clerkPublishableKey) {
    if (!cfg.devAuth) return <div className="p-6 text-danger">Clerk לא מוגדר (CLERK_PUBLISHABLE_KEY).</div>;
    return <TenantGate userLabel="Dev User" />;
  }
  return (
    <ClerkProvider publishableKey={cfg.clerkPublishableKey} localization={heIL} afterSignOutUrl="/"
      appearance={{ elements: {
        // Codes, emails and phone numbers are always LTR, even inside the RTL form.
        otpCodeField: { direction: 'ltr' }, otpCodeFieldInput: { direction: 'ltr' },
        formFieldInput: { direction: 'ltr', textAlign: 'left' },
      } }}>
      {/* Google automatic sign-in / One Tap; needs the Google connection with custom credentials in Clerk. */}
      <GoogleAutoSignIn />
      <SignedOut>
        <div className="flex min-h-full flex-col items-center justify-center gap-4 bg-page p-4">
          <SignIn routing="hash" />
          <LegalLinks />
        </div>
      </SignedOut>
      <SignedIn><WithClerk /></SignedIn>
    </ClerkProvider>
  );
}

function WithClerk() {
  const { getToken, signOut } = useAuth();
  const { user } = useUser();
  const [ready, setReady] = useState(false);
  useEffect(() => { setTokenGetter(() => getToken()); setReady(true); }, [getToken]);
  if (!ready) return null;
  return <TenantGate signOut={() => void signOut()} userLabel={user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? undefined} />;
}

/**
 * Resolves which client (tenant) this session works on: the remembered one, the only one, or a choice.
 * Operators with no clients yet land on the tenants panel to create the first.
 */
function TenantGate({ signOut, userLabel }: { signOut?: () => void; userLabel?: string }) {
  const qc = useQueryClient();
  const { data: me, isLoading, error } = useMe();
  const [selected, setSelected] = useState<string | null>(() => new URLSearchParams(location.search).get('installed') ?? currentTenant());
  const choose = (id: string | null) => { setCurrentTenant(id); setSelected(id); void qc.invalidateQueries(); };
  useEffect(() => { if (selected && selected !== currentTenant()) setCurrentTenant(selected); }, [selected]);

  if (isLoading) return <div className="flex h-full items-center justify-center"><Spinner /></div>;
  if (error || !me) return <div className="p-6 text-danger">{errorText(error)} <button className="btn btn-sm" onClick={signOut}>התנתק</button></div>;

  const valid = selected && me.tenants.some((t) => t.locationId === selected) ? selected : null;
  if (!valid && me.tenants.length === 1) return <AutoChoose id={me.tenants[0].locationId} choose={choose} />;
  if (!valid) {
    return (
      <div className="mx-auto flex min-h-full max-w-3xl flex-col justify-center gap-4 p-4">
        <h1 className="text-xl font-bold">שלום {me.displayName ?? me.email}</h1>
        {me.tenants.length > 1 && <TenantChooser me={me} onChoose={choose} />}
        {me.tenants.length === 0 && !me.superAdmin && (
          <div className="card p-4 text-sm">
            <p>המשתמש <span className="ltr">{me.email}</span> אינו משויך לאף לקוח. בקש ממנהל המערכת אצלך להזמין אותך (לפי כתובת האימייל הזו).</p>
          </div>
        )}
        {me.superAdmin && <TenantsPanel me={me} current={null} onSwitchTenant={choose} />}
        {signOut && <button className="btn self-start" onClick={signOut}>התנתק</button>}
        <LegalLinks />
      </div>
    );
  }
  return <App me={me} signOut={signOut} userLabel={userLabel} onSwitchTenant={choose} />;
}

function AutoChoose({ id, choose }: { id: string; choose: (id: string) => void }) {
  useEffect(() => { choose(id); }, [id, choose]);
  return null;
}

export function TenantChooser({ me, onChoose }: { me: Me; onChoose: (id: string) => void }) {
  return (
    <div className="card p-4">
      <h2 className="mb-2 text-base font-semibold">באיזה לקוח לעבוד?</h2>
      <div className="grid gap-2">
        {me.tenants.map((t) => (
          <button key={t.locationId} className="btn justify-between" onClick={() => onChoose(t.locationId)}>
            <span>{t.name}</span><span className="text-xs text-ink-3">{t.role === 'admin' ? 'מנהל' : 'משתמש'}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// installability: register the worker and catch Chrome's install prompt
startPwa();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <Root />
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
